
import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { useFileUpload } from '@/hooks/useFileUpload';
import { Image, Upload } from 'lucide-react';

interface ImageUploadButtonProps {
  onImageUploaded: (url: string, alt?: string) => void;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg';
  disabled?: boolean;
  children?: React.ReactNode;
}

export const ImageUploadButton: React.FC<ImageUploadButtonProps> = ({
  onImageUploaded,
  variant = 'outline',
  size = 'sm',
  disabled = false,
  children
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { uploadFile, uploading } = useFileUpload({
    bucket: 'article-images',
    allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
    maxSizeBytes: 5 * 1024 * 1024, // 5MB
    onSuccess: (url, fileName) => {
      onImageUploaded(url, fileName);
    }
  });

  const handleFileSelect = (file: File) => {
    if (file) {
      uploadFile(file);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        disabled={disabled || uploading}
        onClick={() => fileInputRef.current?.click()}
      >
        {uploading ? (
          <Upload className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Image className="h-4 w-4 mr-2" />
        )}
        {children || (uploading ? 'Uploading...' : 'Add Image')}
      </Button>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFileSelect(file);
        }}
        className="hidden"
      />
    </>
  );
};
